import { FC, HTMLAttributes, useCallback, useEffect, useState } from 'react'

import { TopBar, Menu, Button } from '@reapit/elements'
import { ChevronDownIcon } from '@reapit/elements/icons/chevron-down'

import {
  LegacyUniversalSearch,
  NavigationMenu,
  OfficeSwitcherMenu,
  SecondaryNavigationMenu,
  UserNavigationMenu,
} from '../types'
import { MainMenu } from './MainMenu'
import { SecondaryMenu } from './SecondaryMenu'
import { UserMenu } from './UserMenu'
import { ModalIframe } from './ModalIframe'
import { UseModalIframeType, IframeHandler } from '../utils'
import { OfficeMenu, OfficeSwitcher } from '@reapit-sales/ui-office-switcher'
import { MainMenuMobile } from './MainMenuMobile'
import { MainLogo } from './MainLogo'
import { AppSwitcher } from './AppSwitcher/AppSwitcher'

interface NavigationProps extends HTMLAttributes<HTMLDivElement> {
  isAppSwitcherEnabled: boolean
  mainLogo: NavigationMenu
  menu: NavigationMenu[]
  secondaryMenu: SecondaryNavigationMenu[]
  userMenu: UserNavigationMenu
  modalController: UseModalIframeType
  universalSearch: LegacyUniversalSearch
  officeMenu: OfficeSwitcherMenu
  selectedOfficeId?: string
  onOfficeChange?: (selectedOffice: OfficeMenu) => void // Updated prop to match OfficeMenu type
}

/**
 * `Navigation` is a top-level component that renders the main application navigation bar.
 * It includes branding, primary and secondary menus, user controls, universal search, and office selection.
 *
 * ### Props:
 * @param menu - An array of primary navigation items.
 * Each item should include:
 * - `itemIndex` (string): Unique key for the menu item.
 * - `title` (string): The label displayed in the navigation.
 * - `onClick` (function): A function to invoke when the item is selected.
 *
 * @param secondaryMenu - An array of secondary navigation items.
 * Each item may include:
 * - `label` (string): Display name for the secondary menu item.
 * - `icon` (ReactNode): Icon to represent the item.
 * - `hasBadge` (boolean): Optional flag to display a badge.
 * - `subMenu` (array): Optional array of submenu items.
 *    Each submenu item should include:
 *    - `label` (string): Display label.
 *    - `onClick` (function): A function to invoke on click.
 *
 * @param userMenu - Configuration for the user profile menu.
 * Includes avatar info and optional actions such as logout or settings.
 *
 * @param modalController - An object returned from the `useModalIframe` hook.
 * It controls iframe visibility, URL, and open/close behavior.
 *
 * @param universalSearch - An object providing a `onClick` function that opens
 * a global search interface. Also supports keyboard shortcuts (⌘+K on macOS, Ctrl+K on Windows/Linux).
 *
 * @param officeMenu - An array of office options for the `OfficeSwitcherMenu`.
 * Each item should include:
 * - `id` (string): Unique ID for the office.
 * - `officeName` (string): Display name for the office.
 *
 * @param selectedOfficeId - Optional ID to determine the default selected office.
 *
 * @param onOfficeChange - Optional onClick function to handle office change.
 *
 * ### Behavior:
 * 1. Renders the main navigation layout with menus, user profile, and modal handling.
 * 2. Provides keyboard shortcut support for launching universal search.
 * 3. Displays a mobile-friendly dropdown menu for navigation items.
 * 4. Renders an `OfficeSwitcher` allowing users to change the current office.
 * 5. Renders a modal iframe driven by `modalController` for embedded content.
 */
export const Navigation: FC<NavigationProps> = ({
  isAppSwitcherEnabled,
  mainLogo,
  menu,
  secondaryMenu,
  userMenu,
  modalController,
  universalSearch,
  officeMenu,
  selectedOfficeId,
  onOfficeChange, // Accept the new prop
  ...restProps
}) => {
  const handleUniversalSearch = useCallback(() => {
    universalSearch.onClick?.()
  }, [universalSearch])

  // To do: Once we have support for keyboard accessiblity in Elements for Universal Search
  // Need to remove below useEffect else it will be doubling the accessibility logic
  const [commandKey, setCommandKey] = useState<string>('⌘K')
  useEffect(() => {
    const isMac = navigator.userAgent.toLowerCase().includes('mac')
    setCommandKey(isMac ? '⌘K' : 'Ctrl+K')

    const handleKeyDown = (event: KeyboardEvent) => {
      const isK = event.key.toLowerCase() === 'k'

      if ((isMac && event.metaKey && isK) || (!isMac && event.ctrlKey && isK)) {
        event.preventDefault()
        handleUniversalSearch()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handleUniversalSearch])

  return (
    <>
      <IframeHandler />
      <TopBar
        {...restProps}
        appSwitcher={isAppSwitcherEnabled && <AppSwitcher />}
        logo={<MainLogo {...mainLogo} />}
        mainNav={<MainMenu menu={menu} />}
        search={
          <TopBar.NavSearch
            button={
              <TopBar.NavSearch.Button
                onClick={(event) => {
                  event.stopPropagation()
                  handleUniversalSearch()
                }}
                shortcut={commandKey}
              />
            }
            iconItem={
              <TopBar.NavSearchIconItem
                onClick={(event) => {
                  event.stopPropagation()
                  handleUniversalSearch()
                }}
              />
            }
          />
        }
        secondaryNav={<SecondaryMenu menu={secondaryMenu} />}
        menu={<MainMenuMobile menu={menu} secondaryMenu={secondaryMenu} userMenu={userMenu} />}
        avatar={<UserMenu menu={userMenu} />}
      />
      <ModalIframe {...modalController} />
      <OfficeSwitcher
        menu={officeMenu}
        selectedOfficeId={selectedOfficeId}
        onChange={onOfficeChange} // Pass the new prop to OfficeSwitcher
      >
        {(switcherOptions) => {
          if (switcherOptions.menu.length > 1) {
            return (
              <>
                <Button
                  hasNoPadding
                  aria-label="Office Switcher"
                  size="medium"
                  variant="tertiary"
                  iconLeft={<ChevronDownIcon />}
                  {...Menu.getTriggerProps({
                    id: 'office-switcher-icon',
                    popoverTarget: 'office-switcher-menu',
                    popoverTargetAction: 'toggle',
                  })}
                ></Button>
                <Menu data-alignment="left" aria-labelledby="office-switcher-icon" id="office-switcher-menu">
                  {switcherOptions.menu.map((officeItem) => (
                    <Menu.Item key={officeItem.id} onClick={switcherOptions.onClick(officeItem)}>
                      {officeItem.name}
                    </Menu.Item>
                  ))}
                </Menu>
              </>
            )
          }
        }}
      </OfficeSwitcher>
    </>
  )
}
